import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { AlertTriangle, ArrowRight, CheckCircle2, UserPlus } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { fmtDate } from "@/lib/checkins";
import { ExpandToggle } from "./ServiceBreakdown";
import { PackAssignDialog } from "./PackAssignDialog";
import { errMsg } from "./kpiFormat";

const FIX = {
  pack_holders: { label: "Assign pack holders", target: "kpi-unmatched-packs" },
  products: { label: "Confirm products", target: "kpi-product-mapping" },
  services: { label: "Confirm services", target: "kpi-service-mapping" },
};

const detail = (fix, i) => {
  if (fix === "pack_holders") {
    return [i.code && `Code ${i.code}`, i.start_at && `started ${fmtDate(i.start_at)}`, i.expires_at && `expires ${fmtDate(i.expires_at)}`,
      i.credits_remaining != null && `${i.credits_remaining} credit${Number(i.credits_remaining) === 1 ? "" : "s"} left`].filter(Boolean).join(" · ");
  }
  if (fix === "services") return `${i.suggested ? `Suggested: ${i.suggested}` : "No suggestion"} · ${i.visits} visit${i.visits === 1 ? "" : "s"} on record`;
  return `Suggested: ${i.suggested}`;
};

const RefreshHint = ({ n, onRefresh }) => {
  const [busy, setBusy] = useState(false);
  const go = async () => {
    setBusy(true);
    try { await onRefresh(); } catch (e) { toast.error(errMsg(e)); setBusy(false); }
  };
  return (
    <div className="flex flex-wrap items-center gap-2 rounded border border-emerald-200 bg-emerald-50 px-2 py-1.5 text-xs text-emerald-900" data-testid="kpi-assign-refresh-hint">
      <span>{n} pack{n === 1 ? "" : "s"} assigned. {onRefresh ? "Refresh the draft to update the figures." : "Figures update the next time this week is recalculated."}</span>
      {onRefresh && <Button size="sm" className="h-6 px-2 text-xs" onClick={go} disabled={busy} data-testid="kpi-assign-refresh-btn">{busy ? "Refreshing…" : "Refresh draft"}</Button>}
    </div>
  );
};

const AssignCell = ({ id, idx, who, onAssign }) => who ? (
  <span className="inline-flex items-center gap-1 text-emerald-700" data-testid={`kpi-warning-${idx}-assigned-${id}`}><CheckCircle2 className="h-3.5 w-3.5" />{who}</span>
) : (
  <button type="button" onClick={onAssign} className="inline-flex items-center gap-1 font-medium text-blue-700 hover:underline" data-testid={`kpi-warning-${idx}-assign-${id}`}>
    <UserPlus className="h-3.5 w-3.5" />Assign</button>
);

function Check({ text, group, idx, canAssign, onRefresh }) {
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [done, setDone] = useState({});
  const [, setParams] = useSearchParams();
  const fix = group && FIX[group.fix];
  const packs = canAssign && group?.fix === "pack_holders";
  const nDone = Object.keys(done).length;
  const go = () => {
    setParams((p) => { const n = new URLSearchParams(p); n.set("tab", "mapping"); return n; }, { replace: true });
    const started = Date.now();
    const tryScroll = (again = true) => {
      const el = document.querySelector(`[data-testid="${fix.target}"]`);
      if (el) {
        window.scrollTo({ top: el.getBoundingClientRect().top + window.scrollY - 96, behavior: "smooth" });
        if (again) setTimeout(() => tryScroll(false), 800);  // sections above may still be loading and push it down
      } else if (Date.now() - started < 4000) setTimeout(tryScroll, 120);
    };
    setTimeout(tryScroll, 120);
  };
  return (
    <li data-testid={`kpi-warning-${idx}`}>
      <div className="flex items-start gap-1">
        {group?.items?.length ? <ExpandToggle open={open} onToggle={() => setOpen((v) => !v)} testId={`kpi-warning-${idx}-toggle`} /> : <span className="mr-1 inline-block w-5" />}
        <span className="pt-0.5">{text}</span>
      </div>
      {open && group && (
        <div className="ml-6 mt-1.5 space-y-2 rounded-md border border-amber-200 bg-white/70 p-2.5" data-testid={`kpi-warning-${idx}-items`}>
          <ul className="max-h-64 space-y-1 overflow-y-auto pr-1 text-xs text-zinc-700">
            {group.items.map((i) => (
              <li key={i.id} className="flex flex-wrap gap-x-2" data-testid={`kpi-warning-${idx}-item-${i.id}`}>
                <span className="font-medium text-zinc-900">{i.name}</span>
                <span className="text-zinc-500">{detail(group.fix, i)}</span>
                {packs && <AssignCell id={i.id} idx={idx} who={done[i.id]} onAssign={() => setEditing(i)} />}
              </li>
            ))}
          </ul>
          {nDone > 0 && <RefreshHint n={nDone} onRefresh={onRefresh} />}
          {fix && <Button size="sm" variant="outline" className="h-7" onClick={go} data-testid={`kpi-warning-${idx}-fix`}>{fix.label}<ArrowRight className="ml-1 h-3.5 w-3.5" /></Button>}
        </div>
      )}
      {editing && <PackAssignDialog row={{ id: editing.id, product: editing.name, code: editing.code }} onClose={() => setEditing(null)}
        onSaved={(p) => { setDone((d) => ({ ...d, [editing.id]: p.name || p.email })); setEditing(null); }} />}
    </li>
  );
}

/** Data checks for a week; checks expand to list the packs, products or appointment types — packs can be assigned in place. */
export const DataChecks = ({ items, groups, canAssign, onRefresh }) => !items?.length ? null : (
  <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900" data-testid="kpi-warnings">
    <div className="flex items-center gap-2 font-medium"><AlertTriangle className="h-4 w-4" />Data checks</div>
    <ul className="mt-1.5 space-y-1">
      {items.map((w, i) => <Check key={w} text={w} idx={i} group={(groups || []).find((g) => g.text === w)} canAssign={canAssign} onRefresh={onRefresh} />)}
    </ul>
  </div>
);
